/**
 * BehaviorNode - base class for behavior tree nodes. Defines status enum
 * (Success, Failure, Running) and core node lifecycle.
 */

import type { ConditionEvaluator } from './behaviors/ConditionEvaluator.js';
import type { Blackboard } from './blackboard/Blackboard.js';

export enum BehaviorStatus {
  Success = 'Success',
  Failure = 'Failure',
  Running = 'Running'
}

export interface BehaviorNodeOptions {
  name?: string;
  description?: string;
  /** Conditions that must be met for this node to execute */
  conditions?: ConditionEvaluator[];
}

export abstract class BehaviorNode {
  public readonly name: string;
  public readonly description: string;
  protected isInitialized: boolean = false;
  protected conditions: ConditionEvaluator[] = [];
  protected currentStatus: BehaviorStatus = BehaviorStatus.Success;
  protected isCurrentlyExecuting: boolean = false;

  constructor(options: BehaviorNodeOptions = {}) {
    this.name = options.name ?? this.constructor.name;
    this.description = options.description ?? '';
    this.conditions = options.conditions ?? [];
  }

  public initialize(blackboard: Blackboard): void {
    if (!this.isInitialized) {
      // Initialize attached conditions
      for (const condition of this.conditions) {
        condition.initialize(blackboard);
      }
      this.onInitialize(blackboard);
      this.isInitialized = true;
    }
  }

  public async execute(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    if (!this.isInitialized) {
      this.initialize(blackboard);
    }

    // Mark as currently executing
    this.isCurrentlyExecuting = true;

    try {
      // Check attached conditions first
      for (const condition of this.conditions) {
        const conditionResult = await condition.evaluate(blackboard);
        if (conditionResult !== BehaviorStatus.Success) {
          // Condition failed, node cannot execute
          this.currentStatus = BehaviorStatus.Failure;
          return this.currentStatus;
        }
      }

      // All conditions passed, execute the node
      this.currentStatus = await this.onExecute(blackboard, deltaTime);
      return this.currentStatus;
    } finally {
      // Mark as no longer executing if not running
      if (this.currentStatus !== BehaviorStatus.Running) {
        this.isCurrentlyExecuting = false;
      }
    }
  }

  public reset(): void {
    this.isCurrentlyExecuting = false;
    this.currentStatus = BehaviorStatus.Success;

    // Reset attached conditions
    for (const condition of this.conditions) {
      condition.reset();
    }
    this.onReset();
  }

  public destroy(): void {
    this.onDestroy();
  }

  protected onInitialize(blackboard: Blackboard): void {}

  protected abstract onExecute(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus>;

  protected onReset(): void {}

  protected onDestroy(): void {}

  public getDebugInfo(): { name: string; description: string; status: string; isExecuting: boolean } {
    return {
      name: this.name,
      description: this.description,
      status: this.currentStatus,
      isExecuting: this.isCurrentlyExecuting
    };
  }

  public isExecuting(): boolean {
    return this.isCurrentlyExecuting;
  }

  public getStatus(): BehaviorStatus {
    return this.currentStatus;
  }

  public getCurrentlyExecutingChild(): BehaviorNode | null {
    return null;
  }

  public getExecutionPath(): BehaviorNode[] {
    const path: BehaviorNode[] = [];

    if (this.isCurrentlyExecuting) {
      path.push(this);

      const currentChild = this.getCurrentlyExecutingChild();
      if (currentChild) {
        path.push(...currentChild.getExecutionPath());
      }
    }

    return path;
  }
}